/**
 * Funciones auxiliares para GridView
 * Contiene funciones de cálculo, formateo y utilidades
 */

/**
 * Formatea el nombre completo del vigilante
 * @param {Object} vigilante - Objeto con datos del vigilante
 * @returns {string} Nombre formateado
 */
export const formatearVigilante = (vigilante) => {
  const nombre = vigilante.nombre || '';
  const apellidos = vigilante.apellidos || '';
  const persona = vigilante.persona || '';
  
  // Construir nombre completo (nombre + apellidos)
  const nombreCompleto = `${nombre} ${apellidos}`.trim();
  
  // Si el nombre contiene "Sin usuario", extraer el código de persona
  if (nombreCompleto.includes('Sin usuario')) {
    const match = nombreCompleto.match(/Sin usuario \((.+)\)/);
    if (match) {
      return `Código: ${match[1]}`;
    }
    return nombreCompleto;
  }
  
  // Si tenemos tanto persona como nombre completo
  if (persona && nombreCompleto && !nombreCompleto.includes('Sin usuario')) {
    return `${persona} - ${nombreCompleto}`;
  } else if (persona) {
    return `Código: ${persona}`;
  } else if (nombreCompleto && !nombreCompleto.includes('Sin usuario')) {
    return nombreCompleto;
  } else {
    return 'Sin nombre';
  }
};

/**
 * Calcula el total de horas trabajadas por un vigilante en el mes
 * @param {string} vigilante - Nombre del vigilante
 * @param {Array} processedEvents - Lista de eventos procesados
 * @param {Date} mesSeleccionado - Mes seleccionado
 * @returns {string} Total de horas con 2 decimales
 */
export const calcularTotalHoras = (vigilante, processedEvents, mesSeleccionado) => {
  const eventosVigilante = processedEvents.filter(event => {
    if (event._pendienteEliminar) return false;
    const eventDate = new Date(event.start);
    const isSameMonth = eventDate.getFullYear() === mesSeleccionado.getFullYear() &&
                       eventDate.getMonth() === mesSeleccionado.getMonth();
    if (!isSameMonth) return false;
    return eventoSobreFila(event, vigilante);
  });

  let totalHoras = 0;
  eventosVigilante.forEach(event => {
    const horasInicio = event.start.getHours();
    const minutosInicio = event.start.getMinutes();
    const horasFin = event.end.getHours();
    const minutosFin = event.end.getMinutes();

    let horas;
    if (horasFin >= horasInicio) {
      horas = horasFin - horasInicio + (minutosFin - minutosInicio) / 60;
    } else {
      horas = (24 - horasInicio) + horasFin + (minutosFin - minutosInicio) / 60;
    }

    totalHoras += horas;
  });

  return totalHoras.toFixed(2);
};

/**
 * Calcula las horas disponibles según el convenio colectivo (160h mensuales)
 * @param {string} vigilante - Nombre del vigilante
 * @param {Array} processedEvents - Lista de eventos procesados
 * @param {Date} mesSeleccionado - Mes seleccionado
 * @returns {Object} {disponibles: string, excedido: boolean}
 */
export const calcularHorasDisponibles = (vigilante, processedEvents, mesSeleccionado) => {
  const HORAS_MAXIMAS_MENSUALES = 160; // Convenio colectivo de vigilantes de seguridad
  const horasTrabajadas = parseFloat(calcularTotalHoras(vigilante, processedEvents, mesSeleccionado));
  const horasDisponibles = HORAS_MAXIMAS_MENSUALES - horasTrabajadas;
  
  return {
    disponibles: horasDisponibles.toFixed(2),
    excedido: horasDisponibles < 0
  };
};

/**
 * Calcula el total de horas por día (8 horas por turno)
 * @param {number|string} dia - Día del mes
 * @param {Array} processedEvents - Lista de eventos procesados
 * @param {Date} mesSeleccionado - Mes seleccionado
 * @returns {Object|null} {total: number, inicio: string, fin: string} o null
 */
export const calcularTotalHorasPorDia = (dia, processedEvents, mesSeleccionado) => {
  // Extraer solo el número del día (puede venir como "9 oct" o "9")
  const diaNumero = typeof dia === 'string' ? parseInt(dia.split(' ')[0]) : dia;
  const diaFormateado = diaNumero.toString().padStart(2, '0');
  const mesFormateado = (mesSeleccionado.getMonth() + 1).toString().padStart(2, '0');
  const fechaBuscada = `${mesSeleccionado.getFullYear()}-${mesFormateado}-${diaFormateado}`;
  
  // Obtener todos los eventos de ese día (excluir pendientes de eliminación)
  const eventosDia = processedEvents.filter(event => !event._pendienteEliminar && event.fecha === fechaBuscada);

  if (eventosDia.length === 0) {
    return null;
  }

  // Calcular 8 horas por cada turno
  const totalHoras = eventosDia.length * 8;

  let horaInicioMinima = null;
  let horaFinMaxima = null;
  let minutosInicioMinimos = null;
  let minutosFinMaximos = null;

  eventosDia.forEach(event => {
    const horasInicio = event.start.getHours();
    const minutosInicio = event.start.getMinutes();
    const horasFin = event.end.getHours();
    const minutosFin = event.end.getMinutes();

    // Determinar hora inicio más temprana
    if (horaInicioMinima === null || 
        horasInicio < horaInicioMinima || 
        (horasInicio === horaInicioMinima && minutosInicio < minutosInicioMinimos)) {
      horaInicioMinima = horasInicio;
      minutosInicioMinimos = minutosInicio;
    }

    // Determinar hora fin más tardía
    if (horaFinMaxima === null || 
        horasFin > horaFinMaxima || 
        (horasFin === horaFinMaxima && minutosFin > minutosFinMaximos)) {
      horaFinMaxima = horasFin;
      minutosFinMaximos = minutosFin;
    }
  });

  const horaInicio = `${horaInicioMinima.toString().padStart(2, '0')}:${minutosInicioMinimos.toString().padStart(2, '0')}`;
  const horaFin = `${horaFinMaxima.toString().padStart(2, '0')}:${minutosFinMaximos.toString().padStart(2, '0')}`;

  return {
    total: totalHoras,
    inicio: horaInicio,
    fin: horaFin
  };
};

/**
 * Calcula las horas totales entre dos horarios
 * @param {string} horaInicio - Hora inicio en formato HH:mm
 * @param {string} horaFin - Hora fin en formato HH:mm
 * @returns {number} Horas totales
 */
export const calcularHorasTotales = (horaInicio, horaFin) => {
  try {
    const [horaIni, minIni] = horaInicio.split(':').map(Number);
    const [horaFinNum, minFin] = horaFin.split(':').map(Number);
    
    const minutosInicio = horaIni * 60 + minIni;
    const minutosFin = horaFinNum * 60 + minFin;
    
    // Manejar turnos que cruzan medianoche
    let diferenciaMinutos = minutosFin - minutosInicio;
    if (diferenciaMinutos < 0) {
      diferenciaMinutos += 24 * 60; // Agregar 24 horas
    }
    
    return diferenciaMinutos / 60; // Convertir a horas
  } catch (error) {
    console.error('Error calculando horas totales:', error);
    return 8; // Valor por defecto
  }
};

/**
 * Obtiene el número de días en un mes
 * @param {Date} date - Fecha del mes
 * @returns {number} Número de días
 */
export const getDaysInMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

/**
 * Genera un array con los días del mes formateados
 * @param {Date} mesSeleccionado - Mes seleccionado
 * @returns {Array<string>} Array de días formateados
 */
const DIAS_SEMANA_INICIAL = ['D', 'L', 'M', 'X', 'J', 'V', 'S']; // Dom, Lun, Mar, Mié, Jue, Vie, Sáb

export const generarDiasDelMes = (mesSeleccionado) => {
  const daysOfMonth = [];
  const ultimoDiaMes = getDaysInMonth(mesSeleccionado);
  for (let i = 1; i <= ultimoDiaMes; i++) {
    const date = new Date(mesSeleccionado.getFullYear(), mesSeleccionado.getMonth(), i);
    const inicial = DIAS_SEMANA_INICIAL[date.getDay()];
    daysOfMonth.push({ dia: i, label: `${i} ${inicial}`, diaSemana: inicial });
  }
  return daysOfMonth;
};

/**
 * Verifica si una celda tiene turno
 * @param {string} vigilante - Nombre del vigilante
 * @param {number} dia - Día del mes
 * @param {Array} processedEvents - Lista de eventos procesados
 * @param {Date} mesSeleccionado - Mes seleccionado
 * @returns {boolean} True si tiene turno
 */
export const tieneTurno = (vigilante, dia, processedEvents, mesSeleccionado) => {
  const diaFormateado = dia.toString().padStart(2, '0');
  const mesFormateado = (mesSeleccionado.getMonth() + 1).toString().padStart(2, '0');
  const fechaBuscada = `${mesSeleccionado.getFullYear()}-${mesFormateado}-${diaFormateado}`;

  const event = processedEvents.find(event => {
    if (event._pendienteEliminar) return false;
    if (event.fecha !== fechaBuscada) return false;
    return eventoSobreFila(event, vigilante);
  });

  return !!event;
};

/**
 * Comprueba si un evento pertenece a la fila (vigilante puede ser string o objeto con eventId/eventIds)
 * @param {Object} event - Evento
 * @param {string|Object} vigilante - Nombre o objeto { nombre, sinAsignar?, eventId?, eventIds? }
 * @returns {boolean}
 */
export const eventoSobreFila = (event, vigilante) => {
  if (!event || event._pendienteEliminar) return false;
  if (typeof vigilante === 'object' && vigilante?.sinAsignar) {
    if (Array.isArray(vigilante.eventIds) && vigilante.eventIds.length > 0) {
      return vigilante.eventIds.includes(event.id);
    }
    if (vigilante.eventId != null) {
      return event.id === vigilante.eventId;
    }
  }
  const name = typeof vigilante === 'string' ? vigilante : vigilante?.nombre;
  if (vigilante === 'Sin Asignar' || name === 'Sin Asignar') {
    return !!event.sinAsignar;
  }
  // Fila asignada con eventIds (varias repeticiones): coincidir por id
  if (typeof vigilante === 'object' && Array.isArray(vigilante.eventIds) && vigilante.eventIds.length > 0) {
    return vigilante.eventIds.includes(event.id);
  }
  // Fila asignada por patrón (persona + horario) sin repeticiones
  if (typeof vigilante === 'object' && vigilante?.nombreBase != null && vigilante?.inicioTurno != null && vigilante?.finTurno != null) {
    const mismoNombre = (event.title === vigilante.nombreBase || event.vigilante === vigilante.nombreBase || event.nombre === vigilante.nombreBase);
    const { inicio: ei, fin: ef } = tramoDesdeEvento(event);
    const mismoTramo = ei === vigilante.inicioTurno && ef === vigilante.finTurno;
    return mismoNombre && mismoTramo;
  }
  return (event.title === name || event.vigilante === name || event.nombre === name);
};

const DIAS_SEMANA_LETRAS = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

/** Normaliza hora a "HH:mm" para que "8:0" y "08:00" agrupen igual. Si no hay dato, devuelve "" */
function normalizarHora(val, fromDate) {
  if (fromDate && (fromDate instanceof Date) && !isNaN(fromDate.getTime())) {
    const h = fromDate.getHours();
    const m = fromDate.getMinutes();
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  if (val == null || val === '') return '';
  const s = String(val).trim();
  const parts = s.split(':').map((p) => parseInt(p, 10) || 0);
  const h = Math.min(23, Math.max(0, parts[0] ?? 0));
  const m = Math.min(59, Math.max(0, parts[1] ?? 0));
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Devuelve { inicio, fin } normalizados desde el evento (campos o start/end) */
function tramoDesdeEvento(event) {
  const inicio = normalizarHora(event.inicioTurno, event.start) || normalizarHora(event.inicioTurno) || '08:00';
  const fin = normalizarHora(event.finTurno, event.end) || normalizarHora(event.finTurno) || '16:00';
  return { inicio, fin };
}

/**
 * Agrupa turnos sin asignar por patrón Y por repetición (índice del tramo en cada día).
 * Ej: si hay 3 turnos 8-16 cada día laborable → 3 filas; cada fila = un mes completo (un turno por día).
 * @param {Array} sinAsignarEvents - Eventos sin asignar
 * @returns {Array} Filas { key, nombre, eventIds, inicioTurno, finTurno }
 */
function agruparTurnosSinAsignarPorPatron(sinAsignarEvents) {
  const porPatron = new Map(); // key = `${inicio}_${fin}` -> array de eventos
  sinAsignarEvents.forEach((event) => {
    const { inicio, fin } = tramoDesdeEvento(event);
    const key = `${inicio}_${fin}`;
    if (!porPatron.has(key)) porPatron.set(key, []);
    porPatron.get(key).push(event);
  });

  const filas = [];
  porPatron.forEach((eventos, patternKey) => {
    const [inicio, fin] = patternKey.split('_');
    const porFecha = new Map(); // fecha -> [eventos ese día]
    eventos.forEach((e) => {
      const f = e.fecha || (e.start ? `${e.start.getFullYear()}-${String(e.start.getMonth() + 1).padStart(2, '0')}-${String(e.start.getDate()).padStart(2, '0')}` : null);
      if (!f) return;
      if (!porFecha.has(f)) porFecha.set(f, []);
      porFecha.get(f).push(e);
    });
    porFecha.forEach((list) => list.sort((a, b) => (a.id || '').localeCompare(b.id || '')));

    const porRepeticion = new Map(); // slotIndex -> eventIds (un turno por día = una fila)
    porFecha.forEach((list) => {
      list.forEach((e, idx) => {
        if (!porRepeticion.has(idx)) porRepeticion.set(idx, []);
        porRepeticion.get(idx).push(e.id);
      });
    });

    const weekdays = new Set();
    eventos.forEach((e) => {
      const fecha = e.fecha ? new Date(e.fecha + 'T12:00:00') : (e.start ? new Date(e.start) : null);
      if (fecha && !isNaN(fecha.getTime())) weekdays.add(fecha.getDay());
    });
    const dias = Array.from(weekdays).sort((a, b) => a - b);
    let labelDias = '';
    if (dias.length === 5 && [1, 2, 3, 4, 5].every((d, i) => d === dias[i])) labelDias = 'L-V';
    else if (dias.length === 7) labelDias = 'L-D';
    else if (dias.length) labelDias = dias.map((d) => DIAS_SEMANA_LETRAS[d]).join('-');
    const baseNombre = labelDias ? `Sin asignar (${labelDias} ${inicio}-${fin})` : `Sin asignar (${inicio}-${fin})`;

    porRepeticion.forEach((eventIds, slotIndex) => {
      const key = `SIN_ASIGNAR_${patternKey}_${slotIndex}`;
      filas.push({
        key,
        nombre: porRepeticion.size > 1 ? `${baseNombre} — Repetición ${slotIndex + 1}` : baseNombre,
        persona: '',
        id: null,
        vigilanteId: null,
        personaId: null,
        nombrePersona: '',
        apellido1: '',
        apellido2: '',
        turno: 'Sin turno',
        sinAsignar: true,
        eventIds,
        inicioTurno: inicio,
        finTurno: fin
      });
    });
  });
  return filas;
}

/**
 * Agrupa turnos asignados solo por persona (vigilante).
 * Una fila = un vigilante; todos sus turnos (con cualquier horario) quedan en la misma fila.
 * Así, al cambiar el horario de un turno, no se mueve a otra fila.
 */
function agruparTurnosAsignadosPorPatronYRepeticion(assignedEvents) {
  const porPersona = new Map(); // key = vigilante (código persona) -> array de eventos
  assignedEvents.forEach((event) => {
    const personaId = event.vigilante || event.title || event.nombre || 'Sin nombre';
    const key = String(personaId).trim();
    if (!porPersona.has(key)) porPersona.set(key, []);
    porPersona.get(key).push(event);
  });

  const filas = [];
  porPersona.forEach((eventos, personaKey) => {
    const first = eventos[0];
    const nombreBase = first.title || first.vigilante || first.nombre || 'Sin nombre';
    const eventIds = eventos.map((e) => e.id);
    const key = `ASIG_${personaKey}`;
    filas.push({
      key,
      nombre: nombreBase,
      nombreBase,
      inicioTurno: first.inicioTurno,
      finTurno: first.finTurno,
      eventIds,
      persona: first.vigilante || '',
      id: first.vigilanteId || first.id || first.vigilante,
      vigilanteId: first.vigilanteId || first.vigilante,
      personaId: first.vigilante || first.personaId,
      nombrePersona: first.nombrePersona || '',
      apellido1: first.apellido1 || '',
      apellido2: first.apellido2 || '',
      turno: first.turno || 'Sin turno',
      sinAsignar: false
    });
  });
  return filas;
}

/**
 * Obtiene los vigilantes únicos con sus datos.
 * Turnos (asignados y sin asignar) se agrupan por patrón y por repetición: cada fila = un mes completo (una repetición del tramo en cada día).
 * @param {Array} processedEvents - Lista de eventos procesados
 * @param {Array} vigilantesSinTurnos - Lista de vigilantes sin turnos
 * @returns {Array} Array de vigilantes únicos
 */
export const obtenerVigilantesUnicos = (processedEvents, vigilantesSinTurnos) => {
  const vigilantesMap = new Map();
  const sinAsignarEvents = processedEvents.filter((e) => !e._pendienteEliminar && e.sinAsignar);
  const assignedEvents = processedEvents.filter((e) => !e._pendienteEliminar && !e.sinAsignar);

  agruparTurnosSinAsignarPorPatron(sinAsignarEvents).forEach((row) => {
    vigilantesMap.set(row.key, row);
  });

  agruparTurnosAsignadosPorPatronYRepeticion(assignedEvents).forEach((row) => {
    vigilantesMap.set(row.key, row);
  });

  // Vigilantes sin turnos
  vigilantesSinTurnos.forEach((v) => {
    if (!vigilantesMap.has(v.nombre)) {
      vigilantesMap.set(v.nombre, {
        nombre: v.nombre,
        persona: v.persona || '',
        id: v.id || v.persona,
        vigilanteId: v.id || v.persona,
        personaId: v.persona || '',
        nombrePersona: v.nombrePersona || v.nombre || '',
        apellido1: v.apellido1 || '',
        apellido2: v.apellido2 || '',
        turno: 'Sin turno',
        sinAsignar: false,
        sinTurnos: true
      });
    }
  });

  return Array.from(vigilantesMap.values());
};

